import React from 'react';
import { Helmet } from 'react-helmet';
import { connect, MapStateToProps } from 'react-redux';
import { RouteComponentProps } from '@reach/router';
import { DataContextProvider } from 'store/context/DataContext';
import {
  appDispatch,
  IAppInstance,
  IProviders,
  ITasks,
  IUsers,
  RootState,
} from 'store/store';
import messages from './Messages';
import DocumentManager from '../Panels/DocumentManager/DocumentManager';
import LogoLoading from '../../../components/LogoLoading/LogoLoading';
import {
  HintIcon,
  HintText,
  QuestionHintBox,
} from 'components/FileManager/ItemsViewer/Survey/SurveyEditor/GeneralQuestion/styles';
import { red } from 'utils/style/colors';
import { ICompany, TProjects } from 'store/companies/companiesTypes';
import { IThisUserState } from 'store/thisUser/thisUserSlice';
import { IProviderItems } from 'store/providers/providerTypes';
import { getCompanyHandler } from 'store/companies/companiesSlice';
import { getAllProviders } from 'store/providers/providerSlice';

const JSDocumentManager: any = DocumentManager;

interface IStateToProps {
  tasks: ITasks;
  users: IUsers;
  company?: ICompany;
  provider: IProviders['map'][0];
  thisUser: IThisUserState;
  projects: TProjects;
  appInstance: IAppInstance;
  providerItems: IProviderItems;
  customFolderId?: number;
}

interface IOwnProps {
  providerId?: string;
  companyId?: string;
  folderId?: string;
  roleId?: string;
  custom?: string;
  pdpId?: string;
}

type IComponentProps = IOwnProps & IStateToProps & RouteComponentProps;

interface IContentState {
  loading: boolean;
  companyTried: boolean;
}

class Contents extends React.PureComponent<IComponentProps, IContentState> {
  constructor(props: IComponentProps) {
    super(props);
    this.state = {
      loading: true,
      companyTried: false,
    };
  }

  componentDidUpdate() {
    this.loadContent();
  }

  componentDidMount() {
    // const path = window.location.pathname;
    // const mobilePath = path.substring(8);
    // const parsed = parse(location.search);
    // if (window.innerWidth < 720 && !parsed.forceContent) {
    //   redirectTo(mobilePath);
    //   return;
    // }
    this.loadContent();
  }

  private loadContent() {
    if (!this.props.provider && this.props.providerId) {
      appDispatch(
        getAllProviders({ providersId: [Number(this.props.providerId)] })
      )
        .then(() => {
          this.setState({ loading: false });
        })
        .catch(() => {
          this.setState({ loading: false });
        });
    }
    const companyId = this.props.provider?.data?.managingCompanyId;
    if (companyId && !this.props.company && !this.state.companyTried) {
      appDispatch(
        getCompanyHandler({
          companyId,
          templateStub: null,
          companyStub: false,
          projectStub: false,
        })
      )
        .then(() => {
          this.setState({ loading: false, companyTried: true });
        })
        .catch(() => {
          this.setState({ loading: false, companyTried: true });
        });
    } else {
      this.setState({ loading: false, companyTried: true });
    }
  }

  render() {
    const {
      users,
      provider,
      providerItems,
      company,
      projects,
      tasks,
      thisUser,
      customFolderId,
    } = this.props;
    if (!provider || provider.isLoading || this.state.loading) {
      return <LogoLoading />;
    }
    const providerLimited =
      (provider &&
        provider.data &&
        provider.data!.configFlag &&
        (provider.data!.configFlag.limited === 'true' ||
          provider.data!.configFlag.limited === 'all')) ||
      company?.configFlag?.limited === 'true';
    return (
      <div style={{ height: '100%' }}>
        <Helmet>
          <title>{messages.contentManagement}</title>
          <meta name="description" content={messages.contentManagement} />
        </Helmet>

        <div className="row" style={{ height: '100%' }}>
          <div id="dm" style={{ height: '100%' }}>
            {providerLimited && (
              <QuestionHintBox style={{ color: red, borderColor: red }}>
                <HintIcon type="info" style={{ color: red }} />
                <HintText style={{ color: red }}>
                  <span>{messages.limitedAccount}</span>
                </HintText>
              </QuestionHintBox>
            )}
            <DataContextProvider
              value={{ providerId: Number(this.props.providerId) }}
            >
              {provider.data && (
                <JSDocumentManager
                  users={users}
                  dispatch={appDispatch}
                  provider={provider.data}
                  providerItems={providerItems}
                  isUserManager={false}
                  company={company}
                  projects={projects}
                  tasks={tasks}
                  userId={Number(thisUser.userId)}
                  roleId={this.props.roleId}
                  rootFolderId={
                    this.props.folderId
                      ? +this.props.folderId
                      : customFolderId || provider.data?.rootDocument?.id
                  }
                />
              )}
            </DataContextProvider>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps: MapStateToProps<IStateToProps, IOwnProps, RootState> = (
  state,
  props
) => {
  const thisUser = state.thisUser;
  const users = state.users;
  const appInstance = state.appInstance;

  const providerId = Number(props.providerId);
  const provider = state.providers.map[providerId];

  const companyId = props.companyId
    ? Number(props.companyId)
    : provider?.data?.managingCompanyId;
  const company = state.companies.data[companyId!];

  const selectedCompanyPdp = company?.pdpPanels?.find(
    (companyPdp) => companyPdp.id === Number(props.pdpId)
  );

  const finalProviderId =
    props.custom === 'pdpDocuments'
      ? Number(selectedCompanyPdp?.properties.classProviderId)
      : providerId;

  const finalProvider = state.providers.map[finalProviderId];
  const providerItems = state.providers.items[finalProviderId];
  const projects = company?.projects;
  const tasks = state.tasks;

  return {
    provider: finalProvider,
    thisUser,
    providerItems,
    users,
    appInstance,
    company,
    projects,
    tasks,
    customFolderId:
      props.custom === 'pdpDocuments'
        ? Number(selectedCompanyPdp?.properties.classHomeFolder)
        : undefined,
  };
};

export default connect(mapStateToProps)(Contents);
